import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, Observable } from "rxjs";
import { TRAINING_URL } from "../shared/src/lib/shared";
import { ErrorHandlerService } from "./error-handler.service";



@Injectable({
    providedIn: 'root',
})
export class TrainingService {

    private readonly TRAINING_URL = `${TRAINING_URL}`;

    constructor(
        private http: HttpClient,
        private errorHandler: ErrorHandlerService
    ) { }

    // Get training types for dropdown (wrapped under /training namespace)
    // Supports single or multiple countryIds
    private dashboardFilterParams(params: {
        years?: number[];
        quarters?: number[];
        months?: number[];
        weeks?: number[];
        countryIds?: number[];
        deliveryMethodIds?: number[];
        trainingTypeIds?: number[];
        attendeeTypes?: string[];
        userIds?: number[];
        titles?: string[];
        productLines?: string[];
        quizTypes?: string[];
        hideTest?: boolean;
        offset?: number;
        limit?: number;
        search?: string;
        scoreBandSrc?: 'quiz' | 'post';
        scoreBandIndex?: number;
    }): HttpParams {
        let query = new HttpParams();
        if (params.years?.length) query = query.set('years', params.years.join(','));
        if (params.quarters?.length) query = query.set('quarters', params.quarters.join(','));
        if (params.months?.length) query = query.set('months', params.months.join(','));
        if (params.weeks?.length) query = query.set('weeks', params.weeks.join(','));
        if (params.countryIds?.length) query = query.set('countryIds', params.countryIds.join(','));
        if (params.deliveryMethodIds?.length) query = query.set('deliveryMethodIds', params.deliveryMethodIds.join(','));
        if (params.trainingTypeIds?.length) query = query.set('trainingTypeIds', params.trainingTypeIds.join(','));
        if (params.attendeeTypes?.length) query = query.set('attendeeTypes', params.attendeeTypes.join('||'));
        if (params.userIds?.length) query = query.set('userIds', params.userIds.join(','));
        if (params.titles?.length) query = query.set('titles', params.titles.join('||'));
        if (params.productLines?.length) query = query.set('productLines', params.productLines.join('||'));
        if (params.quizTypes?.length) query = query.set('quizTypes', params.quizTypes.join('||'));
        query = query.set('hideTest', params.hideTest === false ? 'false' : 'true');
        if (params.offset != null) query = query.set('offset', String(params.offset));
        if (params.limit != null) query = query.set('limit', String(params.limit));
        if (params.search) query = query.set('search', params.search);
        if (params.scoreBandSrc) query = query.set('scoreBandSrc', params.scoreBandSrc);
        if (params.scoreBandIndex != null) query = query.set('scoreBandIndex', String(params.scoreBandIndex));
        return query;
    }

    getTrainingDashboard(params: {
        years?: number[];
        quarters?: number[];
        months?: number[];
        weeks?: number[];
        countryIds?: number[];
        deliveryMethodIds?: number[];
        trainingTypeIds?: number[];
        attendeeTypes?: string[];
        userIds?: number[];
        titles?: string[];
        productLines?: string[];
        quizTypes?: string[];
        hideTest?: boolean;
    }): Observable<any> {
        return this.http.get<any>(`${this.TRAINING_URL}/trainingDashboard`, {
            params: this.dashboardFilterParams(params),
        }).pipe(catchError(this.errorHandler.handleError));
    }

    getTrainingDashboardTrainers(params: {
        years?: number[];
        quarters?: number[];
        months?: number[];
        weeks?: number[];
        countryIds?: number[];
        deliveryMethodIds?: number[];
        trainingTypeIds?: number[];
        attendeeTypes?: string[];
        userIds?: number[];
        titles?: string[];
        productLines?: string[];
        quizTypes?: string[];
        hideTest?: boolean;
        offset?: number;
        limit?: number;
    }): Observable<any> {
        return this.http.get<any>(`${this.TRAINING_URL}/trainingDashboard/trainers`, {
            params: this.dashboardFilterParams(params),
        }).pipe(catchError(this.errorHandler.handleError));
    }

    getTrainingDashboardAttendees(params: {
        years?: number[];
        quarters?: number[];
        months?: number[];
        weeks?: number[];
        countryIds?: number[];
        deliveryMethodIds?: number[];
        trainingTypeIds?: number[];
        attendeeTypes?: string[];
        userIds?: number[];
        titles?: string[];
        productLines?: string[];
        quizTypes?: string[];
        hideTest?: boolean;
        offset?: number;
        limit?: number;
        search?: string;
        scoreBandSrc?: 'quiz' | 'post';
        scoreBandIndex?: number;
    }): Observable<any> {
        return this.http.get<any>(`${this.TRAINING_URL}/trainingDashboard/attendees`, {
            params: this.dashboardFilterParams(params),
        }).pipe(catchError(this.errorHandler.handleError));
    }

    getTrainingDashboardOptionPeople(params: {
        years?: number[];
        quarters?: number[];
        months?: number[];
        weeks?: number[];
        countryIds?: number[];
        deliveryMethodIds?: number[];
        trainingTypeIds?: number[];
        attendeeTypes?: string[];
        userIds?: number[];
        titles?: string[];
        productLines?: string[];
        quizTypes?: string[];
        hideTest?: boolean;
        offset?: number;
        limit?: number;
        search?: string;
    }): Observable<any> {
        return this.http.get<any>(`${this.TRAINING_URL}/trainingDashboard/optionPeople`, {
            params: this.dashboardFilterParams(params),
        }).pipe(catchError(this.errorHandler.handleError));
    }

    getTrainingDashboardOptionTitles(params: {
        years?: number[];
        quarters?: number[];
        months?: number[];
        weeks?: number[];
        countryIds?: number[];
        deliveryMethodIds?: number[];
        trainingTypeIds?: number[];
        attendeeTypes?: string[];
        userIds?: number[];
        titles?: string[];
        productLines?: string[];
        quizTypes?: string[];
        hideTest?: boolean;
        offset?: number;
        limit?: number;
        search?: string;
    }): Observable<any> {
        return this.http.get<any>(`${this.TRAINING_URL}/trainingDashboard/optionTitles`, {
            params: this.dashboardFilterParams(params),
        }).pipe(catchError(this.errorHandler.handleError));
    }

    getTrainingTypesForDropdown(countryId: number | number[], search?: string): Observable<any> {
        const value = Array.isArray(countryId)
            ? countryId.join(',')
            : countryId.toString();

        let params = new HttpParams().set('countryId', value);

        if (search && search.trim() !== '') {
            params = params.set('search', search.trim());
        }

        return this.http.get<any>(`${this.TRAINING_URL}/training/trainingTypes`, { params });
    }

    // Get training modules for Select Module tab based on training type and country
    getTrainingModulesForDropdown(trainingTypeId: number, countryId: number | number[]): Observable<any> {
        const resolvedCountryId = Array.isArray(countryId) ? countryId[0] : countryId;

        let params = new HttpParams()
            .set('trainingTypeId', trainingTypeId.toString())
            .set('countryId', resolvedCountryId.toString());

        return this.http.get<any>(`${this.TRAINING_URL}/training/modules`, { params });
    }

    //get field training list (delivery method is fixed to Field = 2 on backend)
    getFieldTrainingList(params: {
        pageIndex: number;
        pageSize: number;
        searchValue?: string;
        countryIds?: number[];
        startDate?: string;
        endDate?: string;
        trainingTypeIds?: number[];
        accountIds?: number[];
        durationIds?: number[];
        status?: number[];
        trainerIds?: number[];
        moduleIds?: number[];
        formatToolIds?: number[];
        months?: number[];
    }): Observable<any> {
        let queryParams = new HttpParams()
            .set('pageIndex', params.pageIndex)
            .set('pageSize', params.pageSize);

        if (params.searchValue && params.searchValue.trim() !== '') {
            console.log(params.searchValue);
            queryParams = queryParams.set('searchValue', params.searchValue);
        }
        if (params.startDate && params.startDate.trim() !== '') {
            queryParams = queryParams.set('startDate', params.startDate);
        }
        if (params.countryIds && params.countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        if (params.endDate && params.endDate.trim() !== '') {
            queryParams = queryParams.set('endDate', params.endDate);
        }
        if (params.trainingTypeIds && params.trainingTypeIds.length > 0) {
            queryParams = queryParams.set('trainingTypeIds', params.trainingTypeIds.join(','));
        }
        if (params.accountIds && params.accountIds.length > 0) {
            queryParams = queryParams.set('accountIds', params.accountIds.join(','));
        }
        if (params.durationIds && params.durationIds.length > 0) {
            queryParams = queryParams.set('durationIds', params.durationIds.join(','));
        }
        if (params.status && params.status.length > 0) {
            queryParams = queryParams.set('status', params.status.join(','));
        }
        if (params.trainerIds && params.trainerIds.length > 0) {
            queryParams = queryParams.set('trainerIds', params.trainerIds.join(','));
        }
        if (params.moduleIds && params.moduleIds.length > 0) {
            queryParams = queryParams.set('moduleIds', params.moduleIds.join(','));
        }
        if (params.formatToolIds && params.formatToolIds.length > 0) {
            queryParams = queryParams.set('formatToolIds', params.formatToolIds.join(','));
        }
        if (params.months && params.months.length > 0) {
            queryParams = queryParams.set('months', params.months.join(','));
        }

        return this.http.get<any>(`${this.TRAINING_URL}/training/fieldTrainingList`, { params: queryParams });
    }

    // get field training summary list (grouped by batchId)
    getFieldTrainingSummaryList(params: {
        pageIndex: number;
        pageSize: number;
        searchValue?: string;
        countryIds?: number[];
        startDate?: string;
        endDate?: string;
        trainingTypeIds?: number[];
        accountIds?: number[];
        durationIds?: number[];
        status?: number[];
        trainerIds?: number[];
        moduleIds?: number[];
        formatToolIds?: number[];
    }): Observable<any> {
        let queryParams = new HttpParams()
            .set('pageIndex', params.pageIndex)
            .set('pageSize', params.pageSize);

        if (params.searchValue && params.searchValue.trim() !== '') {
            queryParams = queryParams.set('searchValue', params.searchValue);
        }
        if (params.startDate && params.startDate.trim() !== '') {
            queryParams = queryParams.set('startDate', params.startDate);
        }
        if (params.countryIds && params.countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        if (params.endDate && params.endDate.trim() !== '') {
            queryParams = queryParams.set('endDate', params.endDate);
        }
        if (params.trainingTypeIds && params.trainingTypeIds.length > 0) {
            queryParams = queryParams.set('trainingTypeIds', params.trainingTypeIds.join(','));
        }
        if (params.accountIds && params.accountIds.length > 0) {
            queryParams = queryParams.set('accountIds', params.accountIds.join(','));
        }
        if (params.durationIds && params.durationIds.length > 0) {
            queryParams = queryParams.set('durationIds', params.durationIds.join(','));
        }
        if (params.status && params.status.length > 0) {
            queryParams = queryParams.set('status', params.status.join(','));
        }
        if (params.trainerIds && params.trainerIds.length > 0) {
            queryParams = queryParams.set('trainerIds', params.trainerIds.join(','));
        }
        if (params.moduleIds && params.moduleIds.length > 0) {
            queryParams = queryParams.set('moduleIds', params.moduleIds.join(','));
        }
        if (params.formatToolIds && params.formatToolIds.length > 0) {
            queryParams = queryParams.set('formatToolIds', params.formatToolIds.join(','));
        }

        return this.http.get<any>(`${this.TRAINING_URL}/training/fieldTrainingSummaryList`, { params: queryParams });
    }

    /** Get format tools list (for filter dropdown). Backend uses user's country from token. */
    getFormatTools(): Observable<any> {
        return this.http.get<any>(`${this.TRAINING_URL}/formatTool/getAll`);
    }

    // get trainers / TDS users for specific country/countries (for dropdown filters)
    getTrainersByCountry(countryId: number | number[], searchTerm?: string): Observable<any> {
        const value = Array.isArray(countryId)
            ? countryId.join(',')
            : countryId.toString();

        let params = new HttpParams().set('countryId', value);

        if (searchTerm && searchTerm.trim() !== '') {
            params = params.set('search', searchTerm.trim());
        }

        return this.http.get<any>(`${this.TRAINING_URL}/training/trainersByCountry`, { params });
    }

    // get only TDS users for specific country/countries (for dropdown filters)
    getTdsUsersByCountry(countryId: number | number[], searchTerm?: string): Observable<any> {
        const value = Array.isArray(countryId)
            ? countryId.join(',')
            : countryId.toString();

        let params = new HttpParams().set('countryId', value);

        if (searchTerm && searchTerm.trim() !== '') {
            params = params.set('search', searchTerm.trim());
        }

        return this.http.get<any>(`${this.TRAINING_URL}/training/tdsUsersByCountry`, { params });
    }

    // get only TDS users for specific country/countries and journey plan date range (for dropdown filters)
    getTdsAndTrainerUsersByCountryAndJP(
        countryId: number | number[],
        startDate: string,
        endDate: string,
        searchTerm?: string
    ): Observable<any> {
        const value = Array.isArray(countryId)
            ? countryId.join(',')
            : countryId.toString();

        let params = new HttpParams()
            .set('countryId', value)
            .set('startDate', startDate)
            .set('endDate', endDate);

        if (searchTerm && searchTerm.trim() !== '') {
            params = params.set('search', searchTerm.trim());
        }

        return this.http.get<any>(`${this.TRAINING_URL}/training/getTdsAndTrainerUsersByCountryAndJP`, { params });
    }

    getFieldTrainingAttendeesList(params: {
        pageIndex: number;
        pageSize: number;
        searchValue?: string;
        countryIds?: number[];
        startDate?: string;
        endDate?: string;
        trainingTypeIds?: number[];
        accountIds?: number[];
        durationIds?: number[];
        status?: number[];
        trainerIds?: number[];
        promoterCategoryIds?: number[];
        months?: number[];
    }): Observable<any> {
        let queryParams = new HttpParams()
            .set('pageIndex', params.pageIndex)
            .set('pageSize', params.pageSize);

        if (params.searchValue && params.searchValue.trim() !== '') {
            console.log(params.searchValue);
            queryParams = queryParams.set('searchValue', params.searchValue);
        }
        if (params.startDate && params.startDate.trim() !== '') {
            queryParams = queryParams.set('startDate', params.startDate);
        }
        if (params.countryIds && params.countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        if (params.endDate && params.endDate.trim() !== '') {
            queryParams = queryParams.set('endDate', params.endDate);
        }
        if (params.trainingTypeIds && params.trainingTypeIds.length > 0) {
            queryParams = queryParams.set('trainingTypeIds', params.trainingTypeIds.join(','));
        }
        if (params.accountIds && params.accountIds.length > 0) {
            queryParams = queryParams.set('accountIds', params.accountIds.join(','));
        }
        if (params.durationIds && params.durationIds.length > 0) {
            queryParams = queryParams.set('durationIds', params.durationIds.join(','));
        }
        if (params.status && params.status.length > 0) {
            queryParams = queryParams.set('status', params.status.join(','));
        }
        if (params.trainerIds && params.trainerIds.length > 0) {
            queryParams = queryParams.set('trainerIds', params.trainerIds.join(','));
        }
        if (params.promoterCategoryIds && params.promoterCategoryIds.length > 0) {
            queryParams = queryParams.set('promoterCategoryIds', params.promoterCategoryIds.join(','));
        }
        if (params.months && params.months.length > 0) {
            queryParams = queryParams.set('months', params.months.join(','));
        }

        return this.http.get<any>(`${this.TRAINING_URL}/training/getFieldTrainingAttendeesList`, { params: queryParams });
    }

    getFieldTrainingAttendanceSummary(params: {
        pageIndex: number;
        pageSize: number;
        searchValue?: string;
        countryIds?: number[];
        startDate?: string;
        endDate?: string;
        trainingTypeIds?: number[];
        accountIds?: number[];
        durationIds?: number[];
        status?: number[];
        trainerIds?: number[];
        moduleIds?: number[];
        formatToolIds?: number[];
    }): Observable<any> {
        let queryParams = new HttpParams()
            .set('pageIndex', params.pageIndex)
            .set('pageSize', params.pageSize);

        if (params.searchValue?.trim()) {
            queryParams = queryParams.set('searchValue', params.searchValue);
        }
        if (params.countryIds?.length) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        if (params.startDate?.trim()) {
            queryParams = queryParams.set('startDate', params.startDate);
        }
        if (params.endDate?.trim()) {
            queryParams = queryParams.set('endDate', params.endDate);
        }
        if (params.trainingTypeIds?.length) {
            queryParams = queryParams.set('trainingTypeIds', params.trainingTypeIds.join(','));
        }
        if (params.accountIds?.length) {
            queryParams = queryParams.set('accountIds', params.accountIds.join(','));
        }
        if (params.durationIds?.length) {
            queryParams = queryParams.set('durationIds', params.durationIds.join(','));
        }
        if (params.status?.length) {
            queryParams = queryParams.set('status', params.status.join(','));
        }
        if (params.trainerIds?.length) {
            queryParams = queryParams.set('trainerIds', params.trainerIds.join(','));
        }
        if (params.moduleIds?.length) {
            queryParams = queryParams.set('moduleIds', params.moduleIds.join(','));
        }
        if (params.formatToolIds?.length) {
            queryParams = queryParams.set('formatToolIds', params.formatToolIds.join(','));
        }

        return this.http.get<any>(`${this.TRAINING_URL}/training/getFieldTrainingAttendanceSummary`, { params: queryParams });
    }

    getFieldTrainingPromoterDetails(params: {
        pageIndex: number;
        pageSize: number;
        searchValue?: string;
        countryIds?: number[];
        startDate?: string;
        endDate?: string;
        trainingTypeIds?: number[];
        accountIds?: number[];
        durationIds?: number[];
        status?: number[];
        trainerIds?: number[];
        moduleIds?: number[];
        formatToolIds?: number[];
        attendanceStatus?: string;
        promoterCategoryIds?: number[];
    }): Observable<any> {
        let queryParams = new HttpParams()
            .set('pageIndex', params.pageIndex)
            .set('pageSize', params.pageSize);

        if (params.searchValue?.trim()) {
            queryParams = queryParams.set('searchValue', params.searchValue);
        }
        if (params.countryIds?.length) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        if (params.startDate?.trim()) {
            queryParams = queryParams.set('startDate', params.startDate);
        }
        if (params.endDate?.trim()) {
            queryParams = queryParams.set('endDate', params.endDate);
        }
        if (params.trainingTypeIds?.length) {
            queryParams = queryParams.set('trainingTypeIds', params.trainingTypeIds.join(','));
        }
        if (params.accountIds?.length) {
            queryParams = queryParams.set('accountIds', params.accountIds.join(','));
        }
        if (params.durationIds?.length) {
            queryParams = queryParams.set('durationIds', params.durationIds.join(','));
        }
        if (params.status?.length) {
            queryParams = queryParams.set('status', params.status.join(','));
        }
        if (params.trainerIds?.length) {
            queryParams = queryParams.set('trainerIds', params.trainerIds.join(','));
        }
        if (params.moduleIds?.length) {
            queryParams = queryParams.set('moduleIds', params.moduleIds.join(','));
        }
        if (params.formatToolIds?.length) {
            queryParams = queryParams.set('formatToolIds', params.formatToolIds.join(','));
        }
        if (params.attendanceStatus) {
            queryParams = queryParams.set('attendanceStatus', params.attendanceStatus);
        }
        if (params.promoterCategoryIds?.length) {
            queryParams = queryParams.set('promoterCategoryIds', params.promoterCategoryIds.join(','));
        }

        return this.http.get<any>(`${this.TRAINING_URL}/training/getFieldTrainingPromoterDetails`, { params: queryParams });
    }

    exportFieldTrainingAttendanceSummary(params: {
        searchValue?: string;
        startDate?: string;
        endDate?: string;
        trainingTypeIds?: number[];
        accountIds?: number[];
        durationIds?: number[];
        status?: number[];
        trainerIds?: number[];
        countryIds?: number[];
        moduleIds?: number[];
        formatToolIds?: number[];
    }): Observable<Blob> {
        const body = {
            searchValue: params.searchValue,
            startDate: params.startDate,
            endDate: params.endDate,
            trainingTypeIds: params.trainingTypeIds,
            accountIds: params.accountIds,
            durationIds: params.durationIds,
            status: params.status,
            trainerIds: params.trainerIds,
            moduleIds: params.moduleIds,
            formatToolIds: params.formatToolIds,
        };
        let queryParams = new HttpParams();
        if (params.countryIds?.length) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        return this.http
            .post<Blob>(`${this.TRAINING_URL}/training/exportFieldTrainingAttendanceSummary/downloadExcelReport`, body, {
                params: queryParams,
                responseType: 'blob' as 'json',
            })
            .pipe(catchError(this.errorHandler.handleError));
    }

    exportFieldTrainingPromoterDetails(params: {
        searchValue?: string;
        startDate?: string;
        endDate?: string;
        trainingTypeIds?: number[];
        accountIds?: number[];
        durationIds?: number[];
        status?: number[];
        trainerIds?: number[];
        countryIds?: number[];
        moduleIds?: number[];
        formatToolIds?: number[];
        attendanceStatus?: string;
        promoterCategoryIds?: number[];
    }): Observable<Blob> {
        const body = {
            searchValue: params.searchValue,
            startDate: params.startDate,
            endDate: params.endDate,
            trainingTypeIds: params.trainingTypeIds,
            accountIds: params.accountIds,
            durationIds: params.durationIds,
            status: params.status,
            trainerIds: params.trainerIds,
            moduleIds: params.moduleIds,
            formatToolIds: params.formatToolIds,
            attendanceStatus: params.attendanceStatus,
            promoterCategoryIds: params.promoterCategoryIds,
        };
        let queryParams = new HttpParams();
        if (params.countryIds?.length) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        return this.http
            .post<Blob>(`${this.TRAINING_URL}/training/exportFieldTrainingPromoterDetails/downloadExcelReport`, body, {
                params: queryParams,
                responseType: 'blob' as 'json',
            })
            .pipe(catchError(this.errorHandler.handleError));
    }


    getTrainingAndQuizList(countryIds: number[], pageIndex: number, pageSize: number, searchValue: string, startDate: string, endDate: string, type?: number, status?: number): Observable<any> {
        let queryParams = new HttpParams();
        if (countryIds && countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', countryIds.join(','));
        }

        if (pageIndex) {
            queryParams = queryParams.set('pageIndex', pageIndex.toString());
        }
        if (pageSize) {
            queryParams = queryParams.set('pageSize', pageSize.toString());
        }
        if (searchValue) {
            queryParams = queryParams.set('searchValue', searchValue);
        }
        if (startDate) {
            queryParams = queryParams.set('startDate', startDate);
        }
        if (endDate) {
            queryParams = queryParams.set('endDate', endDate);
        }
        if (type !== undefined && type !== null) {
            queryParams = queryParams.set('type', type.toString());
        }
        if (status !== undefined && status !== null) {
            queryParams = queryParams.set('status', status.toString());
        }

        return this.http.get<any>(`${this.TRAINING_URL}/training/getTrainingQuizList`, { params: queryParams });
    }

    getTrainingQuizListByQuizType(
        countryIds: number[],
        pageIndex: number,
        pageSize: number,
        searchValue: string,
        startDate: string,
        endDate: string,
        type?: number,
        status?: number,
        quizTypeIds?: number[],
        trainingTypeIds?: number[],
    ): Observable<any> {
        let queryParams = new HttpParams();
        if (countryIds && countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', countryIds.join(','));
        }

        if (pageIndex) {
            queryParams = queryParams.set('pageIndex', pageIndex.toString());
        }
        if (pageSize) {
            queryParams = queryParams.set('pageSize', pageSize.toString());
        }
        if (searchValue) {
            queryParams = queryParams.set('searchValue', searchValue);
        }
        if (startDate) {
            queryParams = queryParams.set('startDate', startDate);
        }
        if (endDate) {
            queryParams = queryParams.set('endDate', endDate);
        }
        if (type !== undefined && type !== null) {
            queryParams = queryParams.set('type', type.toString());
        }
        if (status !== undefined && status !== null) {
            queryParams = queryParams.set('status', status.toString());
        }
        if (quizTypeIds && quizTypeIds.length > 0) {
            queryParams = queryParams.set('quizTypeIds', quizTypeIds.join(','));
        }
        if (trainingTypeIds && trainingTypeIds.length > 0) {
            queryParams = queryParams.set('trainingTypeIds', trainingTypeIds.join(','));
        }

        return this.http.get<any>(`${this.TRAINING_URL}/training/getTrainingQuizListByQuizType`, { params: queryParams });
    }


    isQuizMandatoryUser(userId?: number, countryId?: number): Observable<any> {

        let params = new HttpParams();
        if (userId) params = params.set('userId', String(userId));
        if (countryId) params = params.set('countryId', String(countryId));
        return this.http
            .get<any>(`${this.TRAINING_URL}/training/isQuizMandatoryUser`, { params })
            .pipe(catchError(this.errorHandler.handleError));
    }

    updateFieldTrainingQuizStatus(countryId: number, groupTraining: boolean, fieldTraining: boolean): Observable<any> {
        return this.http
            .post<any>(`${this.TRAINING_URL}/training/updateFieldTrainingQuizStatus`, {
                countryId,
                groupTraining,
                fieldTraining
            })
            .pipe(catchError(this.errorHandler.handleError));
    }

    // Get all the statuses
    getStatuses(): Observable<any> {
        return this.http.get<any>(`${this.TRAINING_URL}/training/getStatuses`);
    }

    // Get attendees list (quiz/training submissions) filtered by countryIds
    getAttendeesList(
        countryIds?: number[],
        fromDate?: string,
        toDate?: string,
        searchValue?: string,
        pageIndex?: number,
        pageSize?: number,
        type?: number,
        quizTypeIds?: number[],
        trainingTypeIds?: number[],
    ): Observable<any> {
        let queryParams = new HttpParams();
        if (countryIds && countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', countryIds.join(','));
        }
        if (fromDate) {
            queryParams = queryParams.set('fromDate', fromDate);
        }
        if (toDate) {
            queryParams = queryParams.set('toDate', toDate);
        }
        if (searchValue) {
            queryParams = queryParams.set('searchValue', searchValue);
        }
        if (pageIndex) {
            queryParams = queryParams.set('pageIndex', pageIndex.toString());
        }
        if (pageSize) {
            queryParams = queryParams.set('pageSize', pageSize.toString());
        }
        if (type !== undefined && type !== null) {
            queryParams = queryParams.set('type', type.toString());
        }
        if (quizTypeIds && quizTypeIds.length > 0) {
            queryParams = queryParams.set('quizTypeIds', quizTypeIds.join(','));
        }
        if (trainingTypeIds && trainingTypeIds.length > 0) {
            queryParams = queryParams.set('trainingTypeIds', trainingTypeIds.join(','));
        }
        return this.http.get<any>(`${this.TRAINING_URL}/training/getAttendeesList`, { params: queryParams });
    }

    /** Stores mapped to selected trainers via user–store assignment (not journey plan dates). */
    public getStoreByCountryAndAccountForTraining(
        pageIndex: number,
        pageSize: number,
        countryIds: number[],
        accountIds: number[],
        trainerIds: number[],
        searchFilter?: string
    ): Observable<any> {

        let queryParams = new HttpParams()
            .set('pageIndex', pageIndex)
            .set('pageSize', pageSize);

        if (countryIds && countryIds.length > 0) {
            queryParams = queryParams.set('countryId', countryIds.join(','));
        }

        if (accountIds && accountIds.length > 0) {
            queryParams = queryParams.set('accountId', accountIds.join(','));
        }

        if (trainerIds && trainerIds.length > 0) {
            queryParams = queryParams.set('trainerIds', trainerIds.join(','));
        }

        if (searchFilter) {
            queryParams = queryParams.set('searchFilter', searchFilter);
        }

        return this.http
            .get<any>(`${this.TRAINING_URL}/training/getStoreByCountryAndAccountForTraining`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }

    /** Journey-plan stores for field training setup (one row per plan date + store). */
    public getStoreByJourneyPlanForTraining(
        pageIndex: number,
        pageSize: number,
        countryIds: number[],
        trainerIds: number[],
        startDate: string,
        endDate: string,
        searchFilter?: string,
        accountIds?: number[],
    ): Observable<any> {
        let queryParams = new HttpParams()
            .set('pageIndex', pageIndex)
            .set('pageSize', pageSize)
            .set('startDate', startDate)
            .set('endDate', endDate);

        if (countryIds && countryIds.length > 0) {
            queryParams = queryParams.set('countryId', countryIds.join(','));
        }
        if (trainerIds && trainerIds.length > 0) {
            queryParams = queryParams.set('trainerIds', trainerIds.join(','));
        }
        if (accountIds && accountIds.length > 0) {
            queryParams = queryParams.set('accountId', accountIds.join(','));
        }
        if (searchFilter) {
            queryParams = queryParams.set('searchFilter', searchFilter);
        }

        return this.http
            .get<any>(`${this.TRAINING_URL}/training/getStoreByJourneyPlanForTraining`, { params: queryParams })
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Create field training(s) from the setup payload.
     * Creates one training per selected store, with trainers linked.
     */
    createFieldTraining(bodyData: any): Observable<any> {
        return this.http
            .post<any>(`${this.TRAINING_URL}/training/createFieldTraining`, bodyData)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Remove a journey-plan store from field training when that date is unchecked.
     */
    removeJourneyPlanStoreFromFieldTraining(bodyData: {
        userId: number;
        storeId: number;
        date: string;
    }): Observable<any> {
        return this.http
            .post<any>(`${this.TRAINING_URL}/training/removeJourneyPlanStoreFromFieldTraining`, bodyData)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Future field trainings for a user in the checked month and year.
     */
    getFutureFieldTrainingsForJourneyPlan(bodyData: {
        userId: number;
        storeId: number;
        month: number;
        year: number;
        date: string;
    }): Observable<any> {
        return this.http
            .post<any>(`${this.TRAINING_URL}/training/getFutureFieldTrainingsForJourneyPlan`, bodyData)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Assign the checked store to a selected field training.
     */
    assignStoreToFieldTraining(bodyData: {
        trainingId: number;
        userId: number;
        storeId: number;
        date: string;
    }): Observable<any> {
        return this.http
            .post<any>(`${this.TRAINING_URL}/training/assignStoreToFieldTraining`, bodyData)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Update a single field-training row (same body as createFieldTraining plus trainingId).
     */
    updateFieldTraining(bodyData: any): Observable<any> {
        return this.http
            .post<any>(`${this.TRAINING_URL}/training/updateFieldTraining`, bodyData)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Replace an entire field-training batch (delete all by batchId + recreate).
     */
    replaceFieldTrainingBatch(bodyData: any): Observable<any> {
        return this.http
            .post<any>(`${this.TRAINING_URL}/training/replaceFieldTrainingBatch`, bodyData)
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Check if all trainings in a batch are still Scheduled (trainingStatusId = 1).
     * If any training has status != 1, backend returns those rows in nonScheduled.
     */
    checkBatchStatus(batchId: string): Observable<any> {
        return this.http
            .get<any>(
                `${this.TRAINING_URL}/training/batchStatus/${encodeURIComponent(batchId)}`
            )
            .pipe(catchError(this.errorHandler.handleError));
    }

    /** Get aggregated field-training batch details for edit dialog patching */
    getFieldTrainingBatchDetails(batchId: string): Observable<any> {
        return this.http
            .get<any>(
                `${this.TRAINING_URL}/training/fieldTrainingBatchDetails/${encodeURIComponent(batchId)}`
            )
            .pipe(catchError(this.errorHandler.handleError));
    }

    /** Soft-delete all field trainings belonging to a batchId */
    deleteFieldTrainingBatch(batchId: string): Observable<any> {
        return this.http
            .post<any>(`${this.TRAINING_URL}/training/deleteFieldTrainingBatch`, { batchId })
            .pipe(catchError(this.errorHandler.handleError));
    }

    /** Get field-training quiz payload by batchId (for edit dialog quiz patching) */
    getFieldTrainingQuizByBatchId(batchId: string): Observable<any> {
        return this.http
            .get<any>(
                `${this.TRAINING_URL}/training/getFieldTrainingQuizByBatchId/${encodeURIComponent(batchId)}`
            )
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Cancel a training by id (sets status to Cancelled on backend).
     */
    cancelTraining(id: number): Observable<any> {
        return this.http
            .post<any>(`${this.TRAINING_URL}/training/cancel/${id}`, {})
            .pipe(catchError(this.errorHandler.handleError));
    }


    /**
     * Cancel a training by id (sets status to Cancelled on backend).
     */
    cancelFieldTraining(id: number): Observable<any> {
        return this.http
            .post<any>(`${this.TRAINING_URL}/training/cancelFieldTraining/${id}`, {})
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Export stores for training to Excel (same data as setup field training store table).
     */
    exportStoresForTraining(
        countryIds: number[],
        accountIds: number[] | undefined,
        trainerIds: number[],
        searchFilter?: string
    ): Observable<Blob> {
        let queryParams = new HttpParams();
        if (countryIds && countryIds.length > 0) {
            queryParams = queryParams.set('countryId', countryIds.join(','));
        }
        if (accountIds && accountIds.length > 0) {
            queryParams = queryParams.set('accountId', accountIds.join(','));
        }
        if (trainerIds && trainerIds.length > 0) {
            queryParams = queryParams.set('trainerIds', trainerIds.join(','));
        }
        if (searchFilter) {
            queryParams = queryParams.set('searchFilter', searchFilter);
        }
        return this.http
            .get<Blob>(`${this.TRAINING_URL}/training/exportStoresForTraining/downloadExcelReport`, {
                params: queryParams,
                responseType: 'blob' as 'json',
            })
            .pipe(catchError(this.errorHandler.handleError));
    }

    /** Export journey-plan stores for field training setup (includes Date column). */
    exportJourneyPlanStoresForTraining(
        countryIds: number[],
        trainerIds: number[],
        startDate: string,
        endDate: string,
        accountIds?: number[],
        searchFilter?: string,
    ): Observable<Blob> {
        let queryParams = new HttpParams()
            .set('startDate', startDate)
            .set('endDate', endDate);

        if (countryIds && countryIds.length > 0) {
            queryParams = queryParams.set('countryId', countryIds.join(','));
        }
        if (trainerIds && trainerIds.length > 0) {
            queryParams = queryParams.set('trainerIds', trainerIds.join(','));
        }
        if (accountIds && accountIds.length > 0) {
            queryParams = queryParams.set('accountId', accountIds.join(','));
        }
        if (searchFilter) {
            queryParams = queryParams.set('searchFilter', searchFilter);
        }

        return this.http
            .get<Blob>(`${this.TRAINING_URL}/training/exportJourneyPlanStoresForTraining/downloadExcelReport`, {
                params: queryParams,
                responseType: 'blob' as 'json',
            })
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Export field training list to Excel (same filters as getFieldTrainingList).
     */
    exportFieldTrainingList(params: {
        searchValue?: string;
        startDate?: string;
        endDate?: string;
        trainingTypeIds?: number[];
        accountIds?: number[];
        durationIds?: number[];
        status?: number[];
        trainerIds?: number[];
        countryIds?: number[];
        moduleIds?: number[];
        formatToolIds?: number[];
        months?: number[];
    }): Observable<Blob> {
        const body = {
            searchValue: params.searchValue,
            startDate: params.startDate,
            endDate: params.endDate,
            trainingTypeIds: params.trainingTypeIds,
            accountIds: params.accountIds,
            durationIds: params.durationIds,
            status: params.status,
            trainerIds: params.trainerIds,
            moduleIds: params.moduleIds,
            formatToolIds: params.formatToolIds,
            months: params.months,
        };
        let queryParams = new HttpParams();
        if (params.countryIds && params.countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        return this.http
            .post<Blob>(`${this.TRAINING_URL}/training/exportFieldTrainingList/downloadExcelReport`, body, {
                params: queryParams,
                responseType: 'blob' as 'json',
            })
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Export field training attendees list to Excel (same filters as getFieldTrainingAttendeesList).
     */
    exportFieldTrainingAttendees(params: {
        searchValue?: string;
        startDate?: string;
        endDate?: string;
        trainingTypeIds?: number[];
        accountIds?: number[];
        durationIds?: number[];
        status?: number[];
        trainerIds?: number[];
        countryIds?: number[];
        promoterCategoryIds?: number[];
        months?: number[];
    }): Observable<Blob> {
        const body = {
            searchValue: params.searchValue,
            startDate: params.startDate,
            endDate: params.endDate,
            trainingTypeIds: params.trainingTypeIds,
            accountIds: params.accountIds,
            durationIds: params.durationIds,
            status: params.status,
            trainerIds: params.trainerIds,
            promoterCategoryIds: params.promoterCategoryIds,
            months: params.months,
        };
        let queryParams = new HttpParams();
        if (params.countryIds && params.countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        return this.http
            .post<Blob>(`${this.TRAINING_URL}/training/exportFieldTrainingAttendees/downloadExcelReport`, body, {
                params: queryParams,
                responseType: 'blob' as 'json',
            })
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Export quiz list to Excel (same filters as schedule-quiz list; rows/columns match getTrainingQuizListByQuizType).
     */
    exportQuizList(params: {
        searchValue?: string;
        startDate?: string;
        endDate?: string;
        type?: number;
        status?: number;
        countryIds?: number[];
        quizTypeIds?: number[];
        trainingTypeIds?: number[];
    }): Observable<Blob> {
        const body = {
            searchValue: params.searchValue,
            startDate: params.startDate,
            endDate: params.endDate,
            type: params.type,
            status: params.status,
            quizTypeIds: params.quizTypeIds,
            trainingTypeIds: params.trainingTypeIds,
        };
        let queryParams = new HttpParams();
        if (params.countryIds && params.countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        return this.http
            .post<Blob>(`${this.TRAINING_URL}/training/exportQuizList/downloadExcelReport`, body, {
                params: queryParams,
                responseType: 'blob' as 'json',
            })
            .pipe(catchError(this.errorHandler.handleError));
    }

    /**
     * Export attendees list to Excel (same filters as getAttendeesList / schedule-quiz attendees tab).
     */
    exportAttendeesList(params: {
        countryIds: number[];
        fromDate?: string;
        toDate?: string;
        searchValue?: string;
        type?: number;
        quizTypeIds?: number[];
        trainingTypeIds?: number[];
    }): Observable<Blob> {
        let queryParams = new HttpParams();
        if (params.countryIds && params.countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        if (params.fromDate) {
            queryParams = queryParams.set('fromDate', params.fromDate);
        }
        if (params.toDate) {
            queryParams = queryParams.set('toDate', params.toDate);
        }
        if (params.searchValue) {
            queryParams = queryParams.set('searchValue', params.searchValue);
        }
        if (params.type !== undefined && params.type !== null) {
            queryParams = queryParams.set('type', params.type.toString());
        }
        if (params.quizTypeIds && params.quizTypeIds.length > 0) {
            queryParams = queryParams.set('quizTypeIds', params.quizTypeIds.join(','));
        }
        if (params.trainingTypeIds && params.trainingTypeIds.length > 0) {
            queryParams = queryParams.set('trainingTypeIds', params.trainingTypeIds.join(','));
        }
        return this.http
            .post<Blob>(`${this.TRAINING_URL}/training/exportAttendeesList/downloadExcelReport`, {}, {
                params: queryParams,
                responseType: 'blob' as 'json',
            })
            .pipe(catchError(this.errorHandler.handleError));
    }

    getQuizAttendeesSummary(params: {
        pageIndex: number;
        pageSize: number;
        search?: string;
        countryIds?: number[];
        fromDate?: string;
        toDate?: string;
    }): Observable<any> {
        let queryParams = new HttpParams()
            .set('pageIndex', params.pageIndex)
            .set('pageSize', params.pageSize);
        if (params.search?.trim()) {
            queryParams = queryParams.set('search', params.search.trim());
        }
        if (params.countryIds?.length) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        if (params.fromDate?.trim()) {
            queryParams = queryParams.set('fromDate', params.fromDate);
        }
        if (params.toDate?.trim()) {
            queryParams = queryParams.set('toDate', params.toDate);
        }
        return this.http.get<any>(`${this.TRAINING_URL}/training/getQuizAttendeesSummary`, { params: queryParams });
    }

    getQuizAttendeesDetails(params: {
        pageIndex: number;
        pageSize: number;
        search?: string;
        countryIds?: number[];
        fromDate?: string;
        toDate?: string;
        attendanceStatus?: string;
        deliveryMethodIds?: number[];
    }): Observable<any> {
        let queryParams = new HttpParams()
            .set('pageIndex', params.pageIndex)
            .set('pageSize', params.pageSize);
        if (params.search?.trim()) {
            queryParams = queryParams.set('search', params.search.trim());
        }
        if (params.countryIds?.length) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        if (params.fromDate?.trim()) {
            queryParams = queryParams.set('fromDate', params.fromDate);
        }
        if (params.toDate?.trim()) {
            queryParams = queryParams.set('toDate', params.toDate);
        }
        if (params.attendanceStatus?.trim()) {
            queryParams = queryParams.set('attendanceStatus', params.attendanceStatus.trim());
        }
        if (params.deliveryMethodIds?.length) {
            queryParams = queryParams.set('deliveryMethodIds', params.deliveryMethodIds.join(','));
        }
        return this.http.get<any>(`${this.TRAINING_URL}/training/getQuizAttendeesDetails`, { params: queryParams });
    }

    exportQuizAttendeesSummary(params: {
        search?: string;
        countryIds?: number[];
        fromDate?: string;
        toDate?: string;
    }): Observable<Blob> {
        const body = {
            search: params.search,
            fromDate: params.fromDate,
            toDate: params.toDate,
        };
        let queryParams = new HttpParams();
        if (params.countryIds?.length) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        return this.http
            .post<Blob>(`${this.TRAINING_URL}/training/exportQuizAttendeesSummary/downloadExcelReport`, body, {
                params: queryParams,
                responseType: 'blob' as 'json',
            })
            .pipe(catchError(this.errorHandler.handleError));
    }

    exportQuizAttendeesDetails(params: {
        search?: string;
        countryIds?: number[];
        fromDate?: string;
        toDate?: string;
        attendanceStatus?: string;
        deliveryMethodIds?: number[];
    }): Observable<Blob> {
        const body = {
            search: params.search,
            fromDate: params.fromDate,
            toDate: params.toDate,
            attendanceStatus: params.attendanceStatus,
            deliveryMethodIds: params.deliveryMethodIds,
        };
        let queryParams = new HttpParams();
        if (params.countryIds?.length) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        return this.http
            .post<Blob>(`${this.TRAINING_URL}/training/exportQuizAttendeesDetails/downloadExcelReport`, body, {
                params: queryParams,
                responseType: 'blob' as 'json',
            })
            .pipe(catchError(this.errorHandler.handleError));
    }

    //get training by id
    getFieldTrainingQuizById(id: number): Observable<any> {
        return this.http.get(`${this.TRAINING_URL}/training/getFieldTrainingQuizById/${id}`);
    }

    /**
     * Trainings for copy-quiz dropdown (id & name).
     * Optional trainingDeliveryMethodId; when omitted, all delivery methods are returned.
     */
    getTrainingsForCopyQuizDropdown(params: {
        countryIds?: number[];
        trainingDeliveryMethodId?: number;
        searchValue?: string;
        liveQuizOnly?: boolean;
        isFieldTraining?: boolean;
    }): Observable<any> {
        let queryParams = new HttpParams();

        if (params.countryIds && params.countryIds.length > 0) {
            queryParams = queryParams.set('countryIds', params.countryIds.join(','));
        }
        if (
            params.trainingDeliveryMethodId != null &&
            !isNaN(Number(params.trainingDeliveryMethodId))
        ) {
            queryParams = queryParams.set(
                'trainingDeliveryMethodId',
                String(params.trainingDeliveryMethodId)
            );
        }
        if (params.liveQuizOnly) {
            queryParams = queryParams.set('liveQuizOnly', 'true');
        }
        if (params.isFieldTraining) {
            queryParams = queryParams.set('isFieldTraining', 'true');
        }
        if (params.searchValue && params.searchValue.trim() !== '') {
            queryParams = queryParams.set('searchValue', params.searchValue.trim());
        }

        return this.http.get<any>(
            `${this.TRAINING_URL}/training/trainingsForCopyQuizDropdown`,
            { params: queryParams }
        );
    }

    /** Trainings assigned to the logged-in user for one calendar date (popup details). */
    getTrainingsByDate(date: string): Observable<any> {
        const params = new HttpParams().set('date', date);
        return this.http.get<any>(`${this.TRAINING_URL}/training/getAll`, { params });
    }

    getTrainingAttendeesAndCount(trainingId: number): Observable<any> {
        return this.http.get<any>(`${this.TRAINING_URL}/training/getTrainingAttendeesAndCount/${trainingId}`);
    }

    /** Returns the training, including joinCode, and records the current user as an attendee. */
    saveAttendance(trainingId: number): Observable<any> {
        return this.http.get<any>(`${this.TRAINING_URL}/training/saveAttendance/${trainingId}`);
    }

    startTraining(trainingId: number, time: string): Observable<any> {
        return this.http.post<any>(`${this.TRAINING_URL}/training/start`, { trainingId, time });
    }

    endTraining(body: { trainingId: number; attendantCount: number; endTime: string }): Observable<any> {
        return this.http.post<any>(`${this.TRAINING_URL}/training/end`, body);
    }

}
